import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as d3 from 'd3';
// Using in.json for detailed India map data
import { getDomesticReservesByState } from '../services/api';

const IndiaDomesticReservesMap = ({ selectedMineral = 'All', type = 'Reserves' }) => {
  const mapRef = useRef();
  const svgRef = useRef();
  const [allReservesData, setAllReservesData] = useState([]); // Store all data
  const [reservesData, setReservesData] = useState([]); // Filtered data for display
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  // const [stateDetail, setStateDetail] = useState(null);
  const [geoJSONData, setGeoJSONData] = useState(null);
  const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0, content: '' });
  const [selectedStateDetails, setSelectedStateDetails] = useState(null);
  const [stateDetailsLoading, setStateDetailsLoading] = useState(false);
  const [clickPosition, setClickPosition] = useState({ x: 0, y: 0 });

  // Define colors for different minerals
  const mineralColors = {
    'Cobalt': '#286BBB',     // Matte cobalt blue
    'Graphite': '#00847E',   // Matte graphite black
    'Nickel': '#1d3d63',     // Matte nickel gray
    'Lithium': '#883039',     // Orange
    'Rare Earth Elements': '#B16214', // Purple
    'REEs': '#B16214',       // Purple (alternative)
    'default': '#95a5a6'      // Gray
  };

  // Load GeoJSON data
  useEffect(() => {
    const loadGeoJSONData = async () => {
      try {
        const response = await fetch('/in.json');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setGeoJSONData(data);
      } catch (error) {
        console.error('Error loading GeoJSON data:', error);
        setError('Failed to load map data');
      }
    };
    
    loadGeoJSONData();
  }, []);

  // Fetch domestic reserves or production data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log(`Fetching domestic ${type.toLowerCase()} data...`);
        
        const endpoint = type === 'Production' ? '/api/domestic-production' : '/api/domestic-reserves';
        const response = await fetch(endpoint);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('API response:', data);
        
        if (data.success && data.data) {
          let allData = [];
          
          console.log('API response structure:', data.data);
          
          if (type === 'Production') {
            // Handle production data structure
            // Production data is country-level, so we need to fetch reserves data
            // to find which states have this mineral, then distribute production to those states
            if (data.data.production && Array.isArray(data.data.production)) {
              console.log('Processing production data:', data.data.production);
              
              // Fetch reserves data to find states with this mineral
              try {
                const reservesResponse = await fetch('/api/domestic-reserves');
                if (reservesResponse.ok) {
                  const reservesData = await reservesResponse.json();
                  if (reservesData.success && reservesData.data && reservesData.data.states) {
                    // Get production data for selected mineral
                    const productionForMineral = data.data.production.filter(prod => 
                      selectedMineral === 'All' || prod.mineral === selectedMineral
                    );
                    
                    // Find states that have reserves for this mineral
                    const statesWithMineral = new Set();
                    reservesData.data.states.forEach(state => {
                      if (state.reserves && Array.isArray(state.reserves)) {
                        state.reserves.forEach(reserve => {
                          if (selectedMineral === 'All' || reserve.mineral === selectedMineral) {
                            statesWithMineral.add(state.state);
                          }
                        });
                      }
                    });
                    
                    // Distribute production data to states that have reserves for this mineral
                    productionForMineral.forEach(prod => {
                      if (statesWithMineral.size > 0) {
                        // Distribute production equally to all states with reserves
                        const productionPerState = (prod.quantity || 0) / statesWithMineral.size;
                        statesWithMineral.forEach(stateName => {
                          allData.push({
                            mineral: prod.mineral,
                            quantity: productionPerState,
                            reserve_quantity: productionPerState, // Use for map visualization
                            year: prod.year,
                            unit: prod.unit,
                            country: prod.country || 'India',
                            state: stateName,
                            data_source: prod.data_source || 'domestic_production'
                          });
                        });
                      } else {
                        // If no states have reserves, we can't map production data
                        console.warn(`No states found with reserves for ${prod.mineral}. Cannot display production on map.`);
                      }
                    });
                    
                    console.log('Processed production data distributed to states:', allData);
                  } else {
                    console.warn('Could not fetch reserves data to map production to states');
                    allData = [];
                  }
                } else {
                  console.warn('Failed to fetch reserves data for production mapping');
                  allData = [];
                }
              } catch (err) {
                console.error('Error fetching reserves data for production mapping:', err);
                allData = [];
              }
            } else {
              allData = [];
            }
          } else {
            // Handle reserves data structure (existing logic)
            // Handle the expected API format: data.states array
            if (data.data.states && Array.isArray(data.data.states)) {
              console.log('Processing states data:', data.data.states);
              
              // Extract reserves from each state
              data.data.states.forEach(state => {
                if (state.reserves && Array.isArray(state.reserves)) {
                  state.reserves.forEach(reserve => {
                    // Ensure state name is included in each reserve record
                    allData.push({
                      ...reserve,
                      state: state.state // Ensure state name is set
                    });
                  });
                }
              });
              
              console.log('Extracted reserves from states:', allData);
            }
            // Fallback: if data is directly an array
            else if (Array.isArray(data.data)) {
              allData = data.data;
            }
            // Fallback: if data has a 'reserves' property
            else if (data.data.reserves && Array.isArray(data.data.reserves)) {
              allData = data.data.reserves;
            }
            else {
              console.error('Unexpected data format:', data.data);
              allData = [];
            }
          }
          
          console.log(`Processed ${type.toLowerCase()} data:`, allData);
          
          // Store all data
          setAllReservesData(allData);
          
          // Filter data based on selectedMineral
          const filteredData = selectedMineral === 'All' 
            ? allData 
            : allData.filter(item => 
                item.mineral && item.mineral.toLowerCase() === selectedMineral.toLowerCase()
              );
          
          // console.log(`=== ${selectedMineral.toUpperCase()} FILTERING DEBUG ===`);
          // console.log('Selected mineral:', selectedMineral);
          // console.log('Type:', type);
          // console.log(`All ${type.toLowerCase()} count:`, allData.length);
          if (allData.length > 0) {
            // console.log('All minerals in data:', [...new Set(allData.map(r => r.mineral))]);
            // console.log('Filtered data:', filteredData);
            // console.log('Total records:', filteredData.length);
            if (filteredData.length > 0) {
              // console.log('Sample record:', filteredData[0]);
              // console.log('Available minerals:', [...new Set(filteredData.map(r => r.mineral))]);
              if (type === 'Reserves') {
                // console.log('Available states:', [...new Set(filteredData.map(r => r.state))]);
              }
            }
          }
          setReservesData(filteredData);
        } else {
          console.error('Unexpected API response structure:', data);
          setReservesData([]);
        }
      } catch (err) {
        console.error(`Error fetching domestic ${type.toLowerCase()} data:`, err);
        setError(err.message);
        setReservesData([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [type, selectedMineral]); // Re-fetch when type or selectedMineral changes

  // Filter data when selectedMineral changes
  useEffect(() => {
    if (allReservesData.length > 0) {
      const filteredData = selectedMineral === 'All' 
        ? allReservesData 
        : allReservesData.filter(item => 
            item.mineral && item.mineral.toLowerCase() === selectedMineral.toLowerCase()
          );
      
      console.log('=== MINERAL FILTER CHANGE DEBUG ===');
      console.log('Mineral filter applied:', selectedMineral);
      console.log('Type:', type);
      console.log(`All ${type.toLowerCase()} data length:`, allReservesData.length);
      console.log('All minerals in allData:', [...new Set(allReservesData.map(r => r.mineral))]);
      console.log('Filtered data length:', filteredData.length);
      console.log('Available minerals in filtered data:', [...new Set(filteredData.map(r => r.mineral))]);
      
      setReservesData(filteredData);
    }
  }, [selectedMineral, allReservesData]);

  // Handle state click to show details
  const handleStateClick = async (event, stateName, mineralData = null, specificMineral = null) => {
    try {
      setStateDetailsLoading(true);
      
      // Get click position relative to the map container
      const mapRect = mapRef.current.getBoundingClientRect();
      const clickX = event.clientX - mapRect.left;
      const clickY = event.clientY - mapRect.top;
      
      setClickPosition({ x: clickX, y: clickY });
      
      // If we have mineral data from the visualization, use it first
      if (mineralData) {
        let reserves, totalReserves, mineralCount;
        
        if (specificMineral) {
          // Show only the specific mineral that was clicked
          const quantity = mineralData[specificMineral] || 0;
          reserves = [{
            mineral: specificMineral,
            reserve_quantity: quantity,
            district: specificMineral,
            region: stateName,
          }];
          totalReserves = quantity;
          mineralCount = 1;
        } else {
          // Show all minerals for the state (when clicking on state itself)
          reserves = Object.entries(mineralData).map(([mineral, quantity]) => ({
            mineral: mineral,
            reserve_quantity: quantity || 0,
            district: mineral,
            region: stateName,
          }));
          totalReserves = Object.values(mineralData).reduce((sum, qty) => sum + (qty || 0), 0);
          mineralCount = Object.keys(mineralData).length;
        }
        
        setSelectedStateDetails({
          state: stateName,
          reserves: reserves,
          totalReserves: totalReserves,
          mineralCount: mineralCount,
          useVisualizationData: true,
          specificMineral: specificMineral
        });
        return;
      }
      
      // Fallback to API call if no visualization data
      const response = await getDomesticReservesByState(stateName);
      
      if (response.success && response.data) {
        setSelectedStateDetails({
          state: stateName,
          reserves: response.data.reserves || [],
          totalReserves: response.data.reserves?.reduce((sum, reserve) => 
            sum + (reserve.reserve_quantity || 0), 0) || 0,
          mineralCount: new Set(response.data.reserves?.map(r => r.mineral)).size || 0,
          useVisualizationData: false
        });
      }
    } catch (error) {
      console.error('Error fetching state details:', error);
      setSelectedStateDetails({
        state: stateName,
        reserves: [],
        totalReserves: 0,
        mineralCount: 0,
        error: 'Failed to load state details',
        useVisualizationData: false
      });
    } finally {
      setStateDetailsLoading(false);
    }
  };

  // Close state details modal
  const closeStateDetails = () => {
    setSelectedStateDetails(null);
  };

  const renderMap = useCallback(() => {
    if (!mapRef.current || !svgRef.current) {
      return;
    }

    try {
      const width = mapRef.current.clientWidth || 800;
      const height = mapRef.current.clientHeight || 600;

      // Clear previous map
      d3.select(svgRef.current).selectAll("*").remove();

      const svg = d3.select(svgRef.current)
        .attr('width', width)
        .attr('height', height)
        .attr('viewBox', `0 0 ${width} ${height}`)
        .style('background-color', '#ffffff');

      // Check if GeoJSON data is available
      if (!geoJSONData || !geoJSONData.features) {
        // Add fallback loading message
        svg.append('text')
          .attr('x', width / 2)
          .attr('y', height / 2)
          .attr('text-anchor', 'middle')
          .attr('fill', '#374151')
          .attr('font-size', '16px')
          .text(loading ? 'Loading India Map...' : 'India Map - Data Error');
        
        return;
      }

      // Create a simple projection for India
      const projection = d3.geoMercator()
        .center([82, 23]) // Center on India
        .scale(800)
        .translate([width / 2, height / 2]);

      const path = d3.geoPath().projection(projection);

      // Process reserves data into state-mineral mapping
      const stateMineralMap = new Map();
      const allMinerals = new Set();
      
      if (reservesData && reservesData.length > 0) {
        // Group reserves by state and mineral
        const stateReserves = {};
        reservesData.forEach(reserve => {
          const stateName = reserve.state;
          const mineral = reserve.mineral;
          const quantity = reserve.reserve_quantity || 0;
          
          if (!stateReserves[stateName]) {
            stateReserves[stateName] = {};
          }
          
          if (!stateReserves[stateName][mineral]) {
            stateReserves[stateName][mineral] = 0;
          }
          
          stateReserves[stateName][mineral] += quantity;
          allMinerals.add(mineral);
        });
        
        // Convert to Map for easier access
        Object.entries(stateReserves).forEach(([state, minerals]) => {
          stateMineralMap.set(state, Object.keys(minerals));
        });
      } else {
        // Fallback: check if we have the old format data structure
        console.log('No reserves data found, checking for alternative format...');
        console.log('Current reservesData:', reservesData);
      }

      // Initialize variables for legend and state data
      let allReserveValues = [];
      let maxReserve = 1;
      let minReserve = 1;
      let radiusScale = null;
      let stateMineralData = new Map();

      // Process reserves data first to populate stateMineralData
      if (reservesData && reservesData.length > 0) {
        // Group minerals by state name with reserve quantities
        const stateReserves = {};
        reservesData.forEach(reserve => {
          const stateName = reserve.state;
          const mineral = reserve.mineral;
          // Ensure quantity is a valid number, default to 0 if null, undefined, or NaN
          const quantity = (reserve.reserve_quantity && !isNaN(reserve.reserve_quantity)) ? reserve.reserve_quantity : 0;
          
          if (!stateReserves[stateName]) {
            stateReserves[stateName] = {};
          }
          
          if (!stateReserves[stateName][mineral]) {
            stateReserves[stateName][mineral] = 0;
          }
          
          stateReserves[stateName][mineral] += quantity;
        });
        
        // Convert to Map
        Object.entries(stateReserves).forEach(([state, minerals]) => {
          stateMineralData.set(state, minerals);
        });

        // Calculate scaling for circle sizes
        allReserveValues = [];
        stateMineralData.forEach(mineralReserves => {
          Object.values(mineralReserves).forEach(value => {
            if (value > 0) allReserveValues.push(value);
          });
        });
        
        maxReserve = Math.max(...allReserveValues, 1);
        minReserve = Math.min(...allReserveValues, 1);
        
        // Create scale for circle radius (smaller, more manageable sizes)
        radiusScale = d3.scaleSqrt()
          .domain([minReserve, maxReserve])
          .range([4, 12]);
      }

      // Create color scale for minerals
      const mineralColorScale = d3.scaleOrdinal()
        .domain(Array.from(allMinerals))
        .range(Array.from(allMinerals).map(mineral => mineralColors[mineral] || '#6b7280'));

      // Group districts by state to create proper state boundaries
      const stateGroups = new Map();
      geoJSONData.features.forEach(feature => {
        const stateName = feature.properties.name;
        if (!stateGroups.has(stateName)) {
          stateGroups.set(stateName, []);
        }
        stateGroups.get(stateName).push(feature);
      });
      
      console.log('State groups:', stateGroups.size);
      stateGroups.forEach((districts, stateName) => {
        console.log(`State: ${stateName}, Districts: ${districts.length}`);
      });

      // Create merged state geometries by combining district coordinates
      const mergedStates = [];
      stateGroups.forEach((districts, stateName) => {
        if (districts.length === 1) {
          // Single district, use as is
          mergedStates.push({
            type: "Feature",
            properties: { name: stateName },
            geometry: districts[0].geometry
          });
        } else {
          // Multiple districts, create a MultiPolygon by combining all district geometries
          const coordinates = [];
          districts.forEach(district => {
            if (district.geometry.type === "Polygon") {
              coordinates.push(district.geometry.coordinates);
            } else if (district.geometry.type === "MultiPolygon") {
              coordinates.push(...district.geometry.coordinates);
            }
          });
          
          mergedStates.push({
            type: "Feature",
            properties: { name: stateName },
            geometry: {
              type: "MultiPolygon",
              coordinates: coordinates
            }
          });
        }
      });
      
      console.log('Merged states created:', mergedStates.length);

      // // Draw individual districts with light boundaries
      // console.log('Drawing individual districts, count:', geoJSONData.features.length);
      // geoJSONData.features.forEach((district, index) => {
      //   svg.append('path')
      //     .datum(district)
      //     .attr('d', path)
      //     .attr('fill', '#fff') // Light gray fill
      //     .attr('stroke', '#d1d5db') // Very light gray for district boundaries
      //     .attr('stroke-width', 0.5) // Thin district boundary
      //     .attr('opacity', 0.8)
      //     .style('cursor', 'pointer')
      //     .on('click', function(event, d) {
      //       const stateName = d.properties.st_nm;
      //       // Get the actual mineral quantities from stateMineralData instead of just mineral names
      //       const mineralData = stateMineralData.get(stateName);
      //       handleStateClick(event, stateName, mineralData);
      //       setSelectedState(stateName);
      //     });
      // });
      
      // Draw state boundaries with mineral-based fill colors
      console.log('Drawing state boundaries, count:', mergedStates.length);
      mergedStates.forEach((state, index) => {
        console.log(`State ${index}:`, state.properties.name, 'geometry type:', state.geometry.type);
        
        const pathData = path(state);
        console.log('Path data for', state.properties.name, ':', pathData ? 'valid' : 'invalid');
        
        // Determine state fill color based on minerals
        let stateFillColor = '#f8f9fa'; // Default light gray for states with no minerals
        let stateStrokeColor = '#374151'; // Default dark gray for state boundaries
        let stateOpacity = 0.3; // Default opacity for states with no minerals
        
        const stateName = state.properties.name;
        const mineralData = stateMineralData.get(stateName);
        
        if (mineralData && Object.keys(mineralData).length > 0) {
          // Find the dominant mineral (highest quantity) for this state
          const dominantMineral = Object.entries(mineralData).reduce((a, b) => 
            mineralData[a[0]] > mineralData[b[0]] ? a : b
          )[0];
          
          // Use the mineral color for state fill
          stateFillColor = mineralColorScale(dominantMineral);
          stateStrokeColor = '#ffffff'; // White border for better contrast
          stateOpacity = 0.6; // Higher opacity for states with minerals
        }
        
        svg.append('path')
          .datum(state)
          .attr('d', path)
          .attr('fill', stateFillColor) // Fill with mineral color
          .attr('stroke', stateStrokeColor) // Border color
          .attr('stroke-width', 1) // State boundary thickness
          .attr('opacity', stateOpacity)
          .style('cursor', 'pointer')
          .on('click', function(event, d) {
            const stateName = d.properties.name;
            // Get the actual mineral quantities from stateMineralData instead of just mineral names
            const mineralData = stateMineralData.get(stateName);
            handleStateClick(event, stateName, mineralData);
            setSelectedState(stateName);
          });
      });

      // Create circles for each state that has minerals
      if (stateMineralData.size > 0) {
        stateMineralData.forEach((mineralReserves, stateName) => {
          // Find the merged state feature and calculate state centroid
          const stateFeature = mergedStates.find(f => f.properties.name === stateName);
          if (!stateFeature) return;
          
          // Calculate state centroid
          const centroid = path.centroid(stateFeature);
          if (!centroid || isNaN(centroid[0]) || isNaN(centroid[1])) return;
          
          const x = centroid[0];
          const y = centroid[1];
          
          const minerals = Object.keys(mineralReserves);
          
          // Create circles for each mineral in the state - improved layout
          minerals.forEach((mineral, index) => {
            const color = mineralColorScale(mineral);
            const reserveValue = mineralReserves[mineral];
            const radius = radiusScale(reserveValue);
            
            // Smart positioning to avoid overlap
            let offsetX, offsetY;
            
            if (minerals.length === 1) {
              // Single mineral - center it
              offsetX = 0;
              offsetY = 0;
            } else if (minerals.length === 2) {
              // Two minerals - side by side with minimal spacing
              const spacing = 16;
              offsetX = (index === 0) ? -spacing/2 : spacing/2;
              offsetY = 0;
            } else if (minerals.length === 3) {
              // Three minerals - triangle formation
              const positions = [
                { x: 0, y: -8 },     // top
                { x: -10, y: 6 },    // bottom left
                { x: 10, y: 6 }      // bottom right
              ];
              offsetX = positions[index].x;
              offsetY = positions[index].y;
            } else {
              // Four or more minerals - compact grid
              const cols = 2;
              const spacing = 12;
              const col = index % cols;
              const row = Math.floor(index / cols);
              offsetX = (col - 0.5) * spacing;
              offsetY = (row - 0.5) * spacing;
            }
            
            // Create a group for each mineral dot to add shadow effect
            const dotGroup = svg.append('g')
              .attr('class', 'mineral-dot-group')
              .style('cursor', 'pointer');
            
            // Add shadow circle for better visibility
            dotGroup.append('circle')
              .attr('cx', x + offsetX)
              .attr('cy', y + offsetY)
              .attr('r', radius + 1)
              .attr('fill', 'rgba(0, 0, 0, 0.2)')
              .attr('opacity', 0.3);
            
            // Add main mineral dot
            dotGroup.append('circle')
              .attr('cx', x + offsetX)
              .attr('cy', y + offsetY)
              .attr('r', radius)
              .attr('fill', color)
              .attr('stroke', '#ffffff')
              .attr('stroke-width', 2) // Thicker white border for better contrast
              .attr('opacity', 0.95)
              .on('mouseover', function(event) {
                // Highlight the entire group
                dotGroup.selectAll('circle')
                  .attr('opacity', 1)
                  .attr('stroke', '#000000')
                  .attr('stroke-width', 3);

                // Get coordinates relative to the map container
                const [tooltipX, tooltipY] = d3.pointer(event, mapRef.current);
                
                const formattedReserve = reserveValue >= 1000 ? 
                  `${(reserveValue / 1000).toFixed(1)}K tonnes` : 
                  `${reserveValue.toFixed(0)} tonnes`;
                
                const tooltipContent = `${stateName} - ${mineral}: ${formattedReserve}`;
          
                setTooltip({
                  show: true,
                  x: tooltipX + 10,
                  y: tooltipY - 10,
                  content: tooltipContent
                });
              })
              .on('mouseout', function() {
                // Reset the entire group
                dotGroup.selectAll('circle')
                  .attr('opacity', 0.95)
                  .attr('stroke', '#ffffff')
                  .attr('stroke-width', 2);

                setTooltip({ show: false, x: 0, y: 0, content: '' });
              })
              .on('click', function(event) {
                event.stopPropagation(); // Prevent state click
                handleStateClick(event, stateName, mineralReserves, mineral);
                setSelectedState(stateName);
              });
          });
        });
      }

      // Add mineral legend or name display
      if (selectedMineral === 'All' && allMinerals.size > 0) {
        // Show full legend when "All" is selected
        const legend = svg.append('g')
          .attr('class', 'mineral-legend')
          .attr('transform', `translate(${width - 170}, 20)`);

        // Add background rectangle for legend
        const legendHeight = allMinerals.size * 25 + 20;
        legend.append('rect')
          .attr('x', -10)
          .attr('y', -10)
          .attr('width', 150) // Increased width to accommodate new layout
          .attr('height', legendHeight)
          .attr('fill', 'rgba(255, 255, 255, 0.95)')
          .attr('stroke', '#d1d5db')
          .attr('stroke-width', 1)
          .attr('rx', 6)
          .style('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))');

        // Add legend title
        legend.append('text')
          .attr('x', 50)
          .attr('y', 8)
          .attr('font-size', '12px')
          .attr('font-weight', '700')
          .attr('fill', '#374151')
          .attr('text-anchor', 'middle')
          .text('Minerals');

        // Add legend items with both state fill and dot representation
        Array.from(allMinerals).forEach((mineral, index) => {
          const y = 25 + (index * 20);
          const color = mineralColorScale(mineral);
          
          // Add state fill color rectangle
          legend.append('rect')
            .attr('x', 5)
            .attr('y', y - 4)
            .attr('width', 10)
            .attr('height', 8)
            .attr('fill', color)
            .attr('opacity', 0.6)
            .attr('stroke', '#ffffff')
            .attr('stroke-width', 1)
            .attr('rx', 2);
          
          // Add mineral dot circle
          legend.append('circle')
            .attr('cx', 20)
            .attr('cy', y)
            .attr('r', 4)
            .attr('fill', color)
            .attr('stroke', '#ffffff')
            .attr('stroke-width', 1);
          
          // Add mineral name
          legend.append('text')
            .attr('x', 30)
            .attr('y', y + 4)
            .attr('font-size', '11px')
            .attr('font-weight', '500')
            .attr('fill', '#374151')
            .text(mineral);
        });
      } else {
        // Show simple mineral name when specific mineral is selected
        const mineralName = svg.append('g')
          .attr('class', 'mineral-name')
          .attr('transform', `translate(${width - 120}, 20)`);

        // Add background rectangle
        mineralName.append('rect')
          .attr('x', -10)
          .attr('y', -10)
          .attr('width', 100)
          .attr('height', 30)
          .attr('fill', 'rgba(255, 255, 255, 0.95)')
          .attr('stroke', '#d1d5db')
          .attr('stroke-width', 1)
          .attr('rx', 6)
          .style('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))');

        // Add mineral name text
        mineralName.append('text')
          .attr('x', 40)
          .attr('y', 8)
          .attr('font-size', '12px')
          .attr('font-weight', '600')
          .attr('fill', '#374151')
          .attr('text-anchor', 'middle')
          .text(selectedMineral);
      }

    } catch (error) {
      console.error('Error rendering India map:', error);
    }
  }, [reservesData, geoJSONData, loading, mineralColors]);

  useEffect(() => {
    if (mapRef.current && svgRef.current && geoJSONData && !loading) {
      // Add a small delay to ensure the container is fully rendered
      setTimeout(() => {
        renderMap();
      }, 100);
    } else if (!loading) {
      // Retry after a short delay
      setTimeout(() => {
        if (mapRef.current && svgRef.current && geoJSONData) {
          renderMap();
        }
      }, 500);
    }
  }, [reservesData, selectedState, renderMap, geoJSONData, loading]);

  console.log('Component render - loading:', loading, 'error:', error, 'reservesData length:', reservesData.length);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <div className="text-white text-lg">Loading India Map...</div>
          <div className="text-gray-400 text-sm mt-2">Fetching domestic reserves data</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="text-red-400 text-2xl mb-2">⚠️</div>
          <div className="text-white text-lg">Error Loading Map</div>
          <div className="text-gray-400 text-sm mt-2">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div 
        ref={mapRef} 
        className="w-full border border-gray-300 rounded-lg bg-white"
        style={{ height: 'auto', minHeight: '600px' }}
      >
        <svg ref={svgRef} className="w-full" style={{ height: '600px' }}></svg>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90">
            <div className="text-center">
              <div className="text-gray-700 text-lg">Loading India map data...</div>
            </div>
          </div>
        )}
      </div>
      
      {/* Tooltip */}
      {tooltip.show && (
        <div
          className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg pointer-events-none"
          style={{
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          {tooltip.content}
        </div>
      )}

      {/* State Details Modal */}
      {selectedStateDetails && (
        <div className="absolute bg-white dark:bg-gray-800 rounded-lg p-6 w-96 max-h-[500px] overflow-y-auto shadow-2xl border border-gray-200 dark:border-gray-600 z-50 transition-colors duration-200"
             style={{
               ...(() => {
                 const modalWidth = 384; // w-96 = 24rem = 384px
                 const modalHeight = 500; // max-h-[500px]
                 const padding = 20;
                 const offset = 20; // Offset from click position
                 
                 // Position relative to the map container with offset
                 let left = clickPosition.x + offset;
                 let top = clickPosition.y - offset;
                 
                 // Get map container bounds
                 const mapRect = mapRef.current?.getBoundingClientRect();
                 const mapWidth = mapRect?.width || 800;
                 const mapHeight = mapRect?.height || 600;
                 
                 // If modal would go off the right edge, position it to the left of click
                 if (left + modalWidth > mapWidth - padding) {
                   left = clickPosition.x - modalWidth - offset;
                 }
                 
                 // If modal would go off the bottom edge, position it above click
                 if (top + modalHeight > mapHeight - padding) {
                   top = clickPosition.y - modalHeight - offset;
                 }
                 
                 // Ensure modal stays within map container bounds
                 if (left < padding) left = padding;
                 if (top < padding) top = padding;
                 
                 return {
                   left: `${left}px`,
                   top: `${top}px`
                 };
               })()
             }}>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
              {selectedStateDetails.specificMineral ? 
                `${selectedStateDetails.state} - ${selectedStateDetails.specificMineral}` : 
                selectedStateDetails.state
              }
            </h3>
            <button
              onClick={closeStateDetails}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl"
            >
              ✕
            </button>
          </div>
          
          {stateDetailsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="text-2xl mb-2 animate-spin">🔄</div>
                <div className="text-gray-600 dark:text-gray-300">Loading state details...</div>
              </div>
            </div>
          ) : selectedStateDetails.error ? (
            <div className="text-center py-8">
              <div className="text-red-400 text-lg mb-2">⚠️</div>
              <div className="text-gray-600 dark:text-gray-300">{selectedStateDetails.error}</div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* State Summary */}
              <div className="bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {selectedStateDetails.mineralCount}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-300">Minerals</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">
                      {selectedStateDetails.totalReserves && !isNaN(selectedStateDetails.totalReserves) && selectedStateDetails.totalReserves > 0 ? 
                        `${(selectedStateDetails.totalReserves / 1000).toFixed(1)}K` : 
                        'N/A'
                      }
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-300">Total Tonnes</div>
                  </div>
                </div>
              </div>

              {/* Mineral-wise Reserves */}
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white text-sm mb-3">Mineral-wise Reserves</h4>
                {selectedStateDetails.reserves.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {selectedStateDetails.reserves.map((reserve, index) => {
                      // Get mineral name - prioritize mineral field, fallback to district/region for visualization data
                      const mineralName = reserve.mineral || reserve.district || reserve.region || 'Unknown Mineral';
                      const reserveQty = reserve.reserve_quantity || 0;
                      const unit = reserve.units || reserve.unit || 'tonnes';
                      const year = reserve.year;
                      
                      // Format quantity based on value
                      const formatQuantity = (qty) => {
                        if (!qty || isNaN(qty) || qty <= 0) return 'N/A';
                        if (qty >= 1000000) return `${(qty / 1000000).toFixed(2)}M ${unit}`;
                        if (qty >= 1000) return `${(qty / 1000).toFixed(1)}K ${unit}`;
                        return `${qty.toFixed(0)} ${unit}`;
                      };
                      
                      return (
                        <div key={index} className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {mineralName}
                              </div>
                              {year && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  Year: {year}
                                </div>
                              )}
                            </div>
                            <div className="text-right ml-4">
                              <div className="text-sm font-bold text-gray-900 dark:text-white">
                                {formatQuantity(reserveQty)}
                              </div>
                            </div>
                          </div>
                          {(reserve.notes || reserve.data_as_on) && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {reserve.notes}
                              {reserve.data_as_on && !reserve.notes && (
                                <span>Data as on: {new Date(reserve.data_as_on).toLocaleDateString()}</span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                    <div className="text-lg mb-2">📊</div>
                    <div>No detailed reserve data available</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default IndiaDomesticReservesMap;